<script setup lang="ts">
import { usePublicData } from '@/stores/PublicData'
import { useEditorStore } from '@/stores/Editor'
import { getCSSVariable } from '@/helpers/utils'

import BaseCard from '@/components/common/BaseCard.vue'
import TargetIcon from '@/components/icons/TargetIcon.vue'
import TrophyIcon from '@/components/icons/TrophyIcon.vue'

const publicStore = usePublicData()
const editorStore = useEditorStore()
</script>

<template>
  <section class="section">
    <v-container>
      <div class="section-header">
        <h2 class="section-title">
          {{
            publicStore.name
              ? $t('PublicGoalsAchievements.title', { name: publicStore.name })
              : $t('CommunityPage.goalsAchievements.title', { ns: 'local' })
          }}
        </h2>
        <p class="section-description">
          {{
            publicStore.name
              ? $t('PublicGoalsAchievements.description')
              : $t('CommunityPage.goalsAchievements.description', { ns: 'local' })
          }}
        </p>
      </div>
      <div class="wrapper">
        <BaseCard
          v-if="editorStore.editor.goalsAchievements[publicStore.lang]?.goals.length > 0"
          class="card goals-card"
          :title="$t('PublicGoalsAchievements.goals.title')"
          :background-color="getCSSVariable('ui-blue')"
        >
          <template v-slot:content>
            <TargetIcon class="icon" :icon-style="editorStore.editor.icon.style" />
            <ul>
              <template
                v-for="(text, index) in editorStore.editor.goalsAchievements[publicStore.lang]
                  ?.goals"
                :key="index"
              >
                <li v-if="text.length">
                  <p>{{ text }}</p>
                </li>
              </template>
            </ul>
          </template>
        </BaseCard>
        <BaseCard
          v-if="editorStore.editor.goalsAchievements[publicStore.lang]?.achievements.length > 0"
          class="card achievements-card"
          :title="$t('PublicGoalsAchievements.achievements.title')"
        >
          <template v-slot:content>
            <TrophyIcon class="icon" :icon-style="editorStore.editor.icon.style" />
            <ul>
              <template
                v-for="(text, index) in editorStore.editor.goalsAchievements[publicStore.lang]
                  ?.achievements"
                :key="index"
              >
                <li v-if="text.length">
                  <p>{{ text }}</p>
                </li>
              </template>
            </ul>
          </template>
        </BaseCard>
      </div>
    </v-container>
  </section>
</template>

<style lang="scss" scoped>
.wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  > div {
    width: 100%;
    max-width: 530px;

    .community-page & {
      @include media('md-and-up') {
        min-height: 480px;
      }
    }
  }

  @include media('md-and-up') {
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
    gap: 62px;

    > div {
      flex: 1 1 calc(50% - 31px);
    }
  }
}

.card {
  @include media('md-and-up') {
    padding-right: 50px;
  }

  p {
    overflow-wrap: break-word;
    word-wrap: break-word;
    hyphens: auto;
  }

  ul {
    padding: 25px 0 25px 25px;

    @include media('md-and-up') {
      padding: 25px;
    }
  }

  .community-page & {
    background-color: rgba(250, 250, 250, 0.1) !important;
    border: 1px solid #fafafa;
    border-radius: 10px;
    backdrop-filter: blur(10px);
  }
}

.goals-card {
  .icon {
    position: absolute;
    top: -25px;
    right: -25px;
    transform: scale(0.75);

    @include media('md-and-up') {
      top: 10px;
      right: -35px;
      transform: scale(1);
    }
  }
}

.achievements-card {
  .icon {
    position: absolute;
    top: -20px;
    right: -30px;
    transform: scale(0.65);

    @include media('md-and-up') {
      right: -60px;
      bottom: -15px;
      transform: scale(1);
    }

    @include media('lg-and-up') {
      right: -45px;
      bottom: -10px;
    }
  }
}
</style>
